import { CheckCircleOutlined } from '@ant-design/icons';
import { Card, Checkbox, Empty, List, message, Space, Tag, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useI18n } from '../i18n';
import { apiService } from '../services/api';

const { Title, Text } = Typography;

interface Coupon {
  id: number;
  amount: number;
  stackable: boolean;
  status: string;
  source: string;
  description?: string;
  expiresAt?: string;
  createdAt: string;
  usedAt?: string;
  orderNumber?: string;
  minimumAmount?: number; // 最低消费额度
}

interface CouponSelectorProps {
  selectedCoupons: number[];
  onCouponSelectionChange: (selectedCouponIds: number[], totalDiscount: number) => void;
  totalAmount: number;
}

const CouponSelector: React.FC<CouponSelectorProps> = ({
  selectedCoupons,
  onCouponSelectionChange,
  totalAmount
}) => {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(false);
  const { isAuthenticated } = useAuth();
  const { t } = useI18n();

  // 获取可用优惠券
  const fetchAvailableCoupons = async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    try {
      const response = await apiService.getAvailableCoupons();
      if (response.success) {
        setCoupons(response.data);
      }
    } catch (error: any) {
      console.error('获取优惠券失败:', error);
      message.error(t('messages.coupon.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAvailableCoupons();
  }, [isAuthenticated]);

  // 检查优惠券是否可用
  const isCouponAvailable = (coupon: Coupon) => {
    // 检查状态
    if (coupon.status !== 'ACTIVE') {
      return false;
    }

    // 检查是否已过期
    const daysLeft = getDaysUntilExpiry(coupon.expiresAt);
    if (daysLeft < 0) {
      return false;
    }

    // 检查最低消费额度
    if (coupon.minimumAmount != null && coupon.minimumAmount > 0) {
      if (totalAmount < coupon.minimumAmount) {
        return false; // 订单金额未达到最低消费额度
      }
    }

    return true;
  };

  // 处理优惠券选择变化
  const handleCouponChange = (couponId: number, checked: boolean) => {
    const coupon = coupons.find(c => c.id === couponId);

    // 检查优惠券是否可用
    if (checked && coupon) {
      if (!isCouponAvailable(coupon)) {
        // 检查具体原因
        if (coupon.minimumAmount != null && coupon.minimumAmount > 0 && totalAmount < coupon.minimumAmount) {
          message.warning(t('messages.coupon.minimumAmountNotMet')?.replace('{{amount}}', coupon.minimumAmount.toFixed(2)) || `订单金额需满NT$${coupon.minimumAmount.toFixed(2)}才能使用此优惠券`);
        } else {
          message.warning(t('messages.coupon.notAvailable'));
        }
        return;
      }
    }

    let newSelectedCoupons = [...selectedCoupons];

    if (checked) {
      // 添加优惠券
      if (coupon) {
        // 检查是否可以叠加使用
        if (!coupon.stackable && newSelectedCoupons.length > 0) {
          // 如果不可以叠加，先清除其他已选择的优惠券
          newSelectedCoupons = [];
        }
        newSelectedCoupons.push(couponId);
      }
    } else {
      // 移除优惠券
      newSelectedCoupons = newSelectedCoupons.filter(id => id !== couponId);
    }

    // 计算总折扣
    const totalDiscount = newSelectedCoupons.reduce((sum, couponId) => {
      const coupon = coupons.find(c => c.id === couponId);
      return sum + (coupon?.amount || 0);
    }, 0);

    onCouponSelectionChange(newSelectedCoupons, totalDiscount);
  };

  // 格式化金额
  const formatAmount = (amount: number) => {
    return `NT$${amount.toFixed(2)}`;
  };

  // 格式化时间
  const formatDateTime = (dateTime: string | undefined) => {
    if (!dateTime) return '';
    try {
      const date = new Date(dateTime);
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch (error) {
      console.error('日期格式化错误:', error);
      return dateTime;
    }
  };

  // 计算剩余天数
  const getDaysUntilExpiry = (expiresAt: string | undefined) => {
    if (!expiresAt) return 0;
    try {
      const now = new Date();
      const expiry = new Date(expiresAt);

      // 设置时间为当天的开始，避免时间差异影响天数计算
      now.setHours(0, 0, 0, 0);
      expiry.setHours(0, 0, 0, 0);

      const diffTime = expiry.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return Math.max(0, diffDays); // 确保不返回负数
    } catch (error) {
      console.error('计算剩余天数错误:', error);
      return 0;
    }
  };

  // 获取优惠券来源标签
  const getSourceTag = (source: string) => {
    const sourceMap: { [key: string]: { textKey: string; color: string } } = {
      'REFERRAL_FIRST_PURCHASE': { textKey: 'messages.coupon.source.referralFirst', color: 'blue' },
      'REFERRAL_REGULAR_PURCHASE': { textKey: 'messages.coupon.source.referralRegular', color: 'green' },
      'ADMIN_GRANT': { textKey: 'messages.coupon.source.adminGrant', color: 'purple' },
      'SYSTEM_REWARD': { textKey: 'messages.coupon.source.systemReward', color: 'orange' },
    };
    const config = sourceMap[source] || { textKey: source, color: 'default' };
    return <Tag color={config.color}>{t(config.textKey)}</Tag>;
  };

  if (!isAuthenticated) {
    return (
      <Card title={t('messages.coupon.title')}>
        <Empty description={t('messages.coupon.loginRequired')} />
      </Card>
    );
  }

  return (
    <div
    >
      {coupons.length === 0 ? (
        <Empty description={t('messages.coupon.noAvailable')} />
      ) : (
        <List
          dataSource={coupons}
          renderItem={(coupon) => {
            const isSelected = selectedCoupons.includes(coupon.id);
            const daysLeft = getDaysUntilExpiry(coupon.expiresAt);
            const isExpiringSoon = daysLeft > 0 && daysLeft <= 7;
            const isAvailable = isCouponAvailable(coupon);

            return (
              <List.Item
                style={{
                  backgroundColor: isSelected ? '#f6ffed' : (isAvailable ? 'transparent' : '#f5f5f5'),
                  border: isSelected ? '1px solid #52c41a' : (isAvailable ? '1px solid transparent' : '1px solid #d9d9d9'),
                  borderRadius: '6px',
                  padding: '12px',
                  margin: '8px 0',
                  opacity: isAvailable ? 1 : 0.6,
                }}
              >
                <Checkbox
                  checked={isSelected}
                  disabled={!isAvailable}
                  onChange={(e) => handleCouponChange(coupon.id, e.target.checked)}
                  style={{ marginRight: '12px' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <div>
                      <Text strong style={{ color: isAvailable ? '#52c41a' : '#999', fontSize: '18px' }}>
                        {formatAmount(coupon.amount)}
                      </Text>
                      <Space style={{ marginLeft: '12px' }}>
                        {getSourceTag(coupon.source)}
                        {coupon.stackable && isAvailable && (
                          <Tag color="cyan">{t('messages.coupon.stackable')}</Tag>
                        )}
                        {!isAvailable && (
                          <Tag color="red">不可用</Tag>
                        )}
                      </Space>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {formatDateTime(coupon.expiresAt)}
                      </div>
                      {isExpiringSoon && (
                        <div style={{ fontSize: '12px', color: '#fa8c16' }}>
                          {t('messages.coupon.daysLeft').replace('{{days}}', daysLeft.toString())}
                        </div>
                      )}
                    </div>
                  </div>
                  <Text type="secondary" style={{ fontSize: '14px' }}>
                    {coupon.description || t('messages.coupon.noDescription')}
                  </Text>
                  {coupon.minimumAmount != null && coupon.minimumAmount > 0 && (
                    <div style={{ marginTop: '4px' }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {t('messages.coupon.minimumAmount')?.replace('{{amount}}', formatAmount(coupon.minimumAmount)) || `最低消费：${formatAmount(coupon.minimumAmount)}`}
                      </Text>
                    </div>
                  )}
                  {!isAvailable && (
                    <div style={{ marginTop: '8px' }}>
                      <Text type="danger" style={{ fontSize: '12px' }}>
                        {coupon.status !== 'ACTIVE' && t('messages.coupon.statusAbnormal')}
                        {coupon.usedAt && ` ${t('messages.coupon.used')}`}
                        {daysLeft <= 0 && ` ${t('messages.coupon.expired')}`}
                        {coupon.minimumAmount != null && coupon.minimumAmount > 0 && totalAmount < coupon.minimumAmount && 
                          ` ${t('messages.coupon.minimumAmountNotMet')?.replace('{{amount}}', coupon.minimumAmount.toFixed(2)) || `订单金额需满NT$${coupon.minimumAmount.toFixed(2)}`}`}
                      </Text>
                    </div>
                  )}
                </div>
              </List.Item>
            );
          }}
        />
      )}

      {selectedCoupons.length > 0 && (
        <div style={{
          marginTop: '16px',
          padding: '12px',
          backgroundColor: '#f6ffed',
          borderRadius: '6px',
          border: '1px solid #52c41a'
        }}>
          <Space>
            <CheckCircleOutlined style={{ color: '#52c41a' }} />
            <Text strong>
              {t('messages.coupon.selectedCount').replace('{{count}}', selectedCoupons.length.toString())}
              <Text style={{ color: '#52c41a', fontSize: '16px', marginLeft: '8px' }}>
                NT${selectedCoupons.reduce((sum, couponId) => {
                  const coupon = coupons.find(c => c.id === couponId);
                  return sum + (coupon?.amount || 0);
                }, 0).toFixed(2)}
              </Text>
            </Text>
          </Space>
        </div>
      )}
    </div>
  );
};

export default CouponSelector;
